import {ChangeDetectorRef, Component, DoCheck, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, ParamMap, Router} from "@angular/router";
import {Subscription} from "rxjs";
import {take} from "rxjs/operators";
import {NetscopeApiService, Response, Success} from "@app/services/netscope-api.service";
import {NetscopeUiService} from "@app/services/netscope-ui.service";
import {FlowsRepository} from "@app/netscope/model/flows-repository";
import {AppsGroups, AppsGroupsData, TemporalParameter} from "@app/netscope/model/data-models";
import {AppsGroupsViewState} from "@app/netscope/netscope-common-flowview/netscope-common-flowview.component";
import {
  resolveIncludedAppsGroups,
  setUuidForAppsGroups
} from "@app/netscope/netscope-common-flowview/misc/AppsGroupsLoading";
import {AppsGroupsInventory} from "@app/netscope/netscope-common-flowview/misc/AppsGroupsInventory";
import {AppsGroupsMapComponent} from "@app/netscope/netscope-apps-groups-viewer/apps-groups-map/apps-groups-map.component";
import {
  AppsGroupsFileItem,
  AppsGroupsFilesNavComponent
} from "@app/netscope/netscope-apps-groups-viewer/apps-groups-files-nav/apps-groups-files-nav.component";
import {
  AppsGroupRef,
  AppsGroupsEditing,
  MemberSource
} from "@app/netscope/netscope-apps-groups-viewer/apps-groups-editing";
import {EditorSection} from "@app/netscope/netscope-apps-groups-viewer/apps-group-editor/apps-group-editor.component";
import {
  NewAppsGroupRequest
} from "@app/netscope/netscope-apps-groups-viewer/new-apps-group-dialog/new-apps-group-dialog.component";
import "@app/netscope/netscope-apps-groups-viewer/apps-groups-icons";

export type AppsGroupsViewerTab = "visualisation" | "groups" | "flows";

// How each tab is named in the address of the page: apps-groups/<file>/<mode>[/<group>]
const TAB_OF_MODE: { [mode: string]: AppsGroupsViewerTab } = {view: "visualisation", edit: "groups", flows: "flows"};
const MODE_OF_TAB: { [tab: string]: string } = {visualisation: "view", groups: "edit", flows: "flows"};

/**
 * Apps groups page: the apps groups files on the side, and for the selected file the map of its groups, the
 * groups themselves and the flows between them.
 *
 * The page holds the apps groups of the file as the user edits them (see AppsGroupsEditing), and their display
 * state: the map draws them, and the controls of the page change them. Nothing is written until the user saves.
 *
 * The address of the page follows what it shows: apps-groups/<file>/view, apps-groups/<file>/edit[/<group>] or
 * apps-groups/<file>/flows. It can be bookmarked or shared, and the history of the browser goes back through it.
 */
@Component({
  standalone: false,
  selector: 'app-netscope-apps-groups-viewer',
  templateUrl: './netscope-apps-groups-viewer.component.html',
  styleUrls: ['./netscope-apps-groups-viewer.component.css']
})
export class NetscopeAppsGroupsViewerComponent implements OnInit, DoCheck, OnDestroy {

  @ViewChild('map')
  map: AppsGroupsMapComponent;

  @ViewChild('filesNav')
  filesNav: AppsGroupsFilesNavComponent;

  // The same file as the one the flows view opens on
  selectedFile: string = "networks";
  isLoading = false;
  failureMode = false;

  inventory: AppsGroupsInventory = new AppsGroupsInventory();
  private inventoryLoaded: Promise<boolean>;

  files: AppsGroupsFileItem[] = [];
  // Apps groups of every file as they are saved, indexed by file: a group can include one of another file
  savedGroupsByFile: Map<string, AppsGroups[]> = new Map();
  // The groups of the displayed file, as the user edits them
  editing: AppsGroupsEditing;
  selectedGroup: AppsGroups;
  focusSection: EditorSection;

  // Groups drawn by the map: the edited ones, their inclusions resolved. They are rebuilt when the map shows.
  appsGroups: AppsGroups[];
  private mapContent: string;

  // File the user asked to open while the displayed one has unsaved changes, and what to do once it is open
  pendingFile: string;
  private pendingAction: () => void;

  isSaving = false;
  saveError: string;

  confirmFileDeletion = false;
  isDeletingFile = false;

  newGroupOpen = false;

  // Number of flows descriptions using each apps group, keyed by 'file/name'
  descriptionsUsage: Map<string, number> = new Map();

  selectedTab: AppsGroupsViewerTab = "visualisation";
  tabs: { key: AppsGroupsViewerTab, label: string, count: () => number }[] = [
    {key: "visualisation", label: "Visualisation", count: () => this.shownGroupsCount},
    {key: "groups", label: "Groups", count: () => this.editing?.groups.length},
    {key: "flows", label: "Flows table", count: () => this.flowsRepository?.totalItemsCount},
  ];

  // Display state of each file shown so far, so that coming back to a file finds it as it was left
  viewStatesByFile: Map<string, AppsGroupsViewState> = new Map();
  viewState: AppsGroupsViewState = new AppsGroupsViewState();

  // Flows of a single group, when the user asked for them in the flows table
  flowsFilterGroup: string;
  private filteredFlowsRepository: FlowsRepository;
  temporalParameter: TemporalParameter;

  // Address of the page as it was last set, by the page or by the browser, as 'file/mode/group'
  private urlState: string;
  // Group named in the address as it was last set, to tell a renamed group from another group
  private urlGroup: AppsGroups;
  private subscriptions: Subscription[] = [];

  constructor(private netscopeService: NetscopeApiService, private netscopeUiService: NetscopeUiService,
              private ref: ChangeDetectorRef, private route: ActivatedRoute, private router: Router) {
  }

  ngOnInit(): void {
    // The title bar of the main component shows them: it is changed once that component is checked
    Promise.resolve().then(() => {
      this.netscopeUiService.setViewLabel("apps_groups_name");
      this.netscopeUiService.setIconShape("node-group");
    });

    this.subscriptions.push(this.netscopeUiService.temporalParametersSubject.subscribe((temporalParameter) => {
      this.temporalParameter = temporalParameter;
    }));

    this.inventoryLoaded = this.inventory.load(this.netscopeService);
    this.loadDescriptionsUsage();
    this.subscriptions.push(this.route.paramMap.subscribe((params) => this.onUrlChanged(params)));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  // ---------------------------------------------------------------- Address of the page

  // What the page shows, as its address names it
  private get shownState(): string {
    let mode = MODE_OF_TAB[this.selectedTab];
    let group = this.selectedTab == "groups" ? this.selectedGroup?.name : undefined;
    return [this.selectedFile, mode].concat(group != undefined ? [group] : []).join("/");
  }

  /**
   * Shows what the address names: on the first display, when the user goes back or forward in the history, or
   * follows a link to the page. Without a file it keeps the displayed one, or opens the default one.
   */
  private onUrlChanged = (params: ParamMap) => {
    let file = params.get("file") ?? (this.editing != undefined ? this.selectedFile : "networks");
    let tab = TAB_OF_MODE[params.get("mode")] ?? (params.has("file") ? "visualisation" : this.selectedTab);
    let groupName = tab == "groups" ? params.get("group") : undefined;
    let complete = params.has("file") && params.has("mode") && TAB_OF_MODE[params.get("mode")] != undefined;
    // An incomplete address is replaced by the full one of what is shown
    this.urlState = complete ? [file, params.get("mode")].concat(groupName != undefined ? [groupName] : []).join("/") : undefined;

    if (this.editing != undefined && this.urlState == this.shownState) {
      this.urlGroup = this.selectedGroup;
      return;
    }

    let show = () => {
      if (groupName != undefined) {
        this.selectGroup(this.editing.groups.find((group) => group.name == groupName));
      }
      this.selectTab(tab);
      this.urlGroup = this.selectedGroup;
    };
    if (this.editing == undefined && !this.isLoading) {
      this.loadFile(file).then((loaded) => loaded && show());
    } else {
      this.openFile(file, show);
    }
  }

  /**
   * Sets the address to what the page shows, once it changed: a new entry of the history for another file, tab
   * or group, the same one when the group was only renamed or the address was incomplete.
   */
  ngDoCheck(): void {
    if (this.editing == undefined || this.isLoading || this.pendingFile != undefined) {
      return;
    }
    let state = this.shownState;
    if (state == this.urlState) {
      return;
    }

    let renamed = this.urlGroup != undefined && this.urlGroup === this.selectedGroup && this.selectedTab == "groups";
    let replaceUrl = renamed || this.urlState == undefined;
    this.urlState = state;
    this.urlGroup = this.selectedGroup;
    this.router.navigate(["/apps-groups"].concat(state.split("/")),
                         {queryParamsHandling: "preserve", replaceUrl: replaceUrl});
  }

  // The browser asks before leaving the page with unsaved changes
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.editing?.dirty) {
      event.preventDefault();
    }
  }

  // ---------------------------------------------------------------- Files

  onFilesLoaded = (files: AppsGroupsFileItem[]) => {
    this.files = files;
  }

  get userFiles(): string[] {
    return this.files.filter((file) => !file.generated).map((file) => file.name);
  }

  get selectedFileIsGenerated(): boolean {
    return this.files.some((file) => file.name == this.selectedFile && file.generated);
  }

  get shownGroupsCount(): number {
    return this.editing?.groups.filter((group) => !this.viewState.hiddenAppsGroups.has(group.name)).length;
  }

  get fileSubtitle(): string {
    if (this.editing == undefined) {
      return "";
    }
    let count = this.editing.groups.length;
    let groups = count == 1 ? "1 group" : `${count} groups`;
    return this.selectedFileIsGenerated
      ? `Generated by DC Netscope · ${groups}`
      : `${groups} · flows of the selected period`;
  }

  /**
   * Opens a file, then runs what was asked with it. When the displayed file has unsaved changes the user is
   * asked first whether to save or discard them.
   */
  openFile = (file: string, then: () => void = () => {}) => {
    if (file == this.selectedFile && this.editing != undefined) {
      then();
      return;
    }
    if (this.editing?.dirty) {
      this.pendingFile = file;
      this.pendingAction = then;
      return;
    }
    this.loadFile(file).then((loaded) => loaded && then());
  }

  onFileSelected = (file: string) => {
    if (file != undefined) {
      this.openFile(file);
    }
  }

  saveAndOpenPending = async () => {
    if (await this.save()) {
      this.openPending();
    }
  }

  discardAndOpenPending = () => {
    this.editing.discard();
    this.openPending();
  }

  stayOnFile = () => {
    this.pendingFile = undefined;
    this.pendingAction = undefined;
  }

  private openPending = () => {
    let file = this.pendingFile;
    let then = this.pendingAction ?? (() => {});
    this.stayOnFile();
    this.loadFile(file).then((loaded) => loaded && then());
  }

  private fetchGroups = async (file: string): Promise<AppsGroups[]> => {
    let response: Response<AppsGroupsData> = await this.netscopeService.getAppsGroups(file).pipe(take(1)).toPromise();
    return response instanceof Success ? (response.element?.apps_groups ?? []) : undefined;
  }

  // Loads the groups of every file not loaded yet, which the groups of the displayed one may include
  private loadAllFiles = async () => {
    let filesResponse = await this.netscopeService.getAppsGroupsFiles().pipe(take(1)).toPromise();
    if (!(filesResponse instanceof Success)) {
      return;
    }
    let missing = filesResponse.element.files.filter((file) => !this.savedGroupsByFile.has(file));
    let groups = await Promise.all(missing.map((file) => this.fetchGroups(file)));
    missing.forEach((file, index) => {
      if (groups[index] != undefined) {
        this.savedGroupsByFile.set(file, groups[index]);
      }
    });
  }

  /**
   * Shows a file. False when another file was asked for while it was loading: what was to be done once it is
   * shown must then not be done, as the page shows the other one.
   */
  loadFile = async (file: string): Promise<boolean> => {
    this.selectedFile = file;
    this.viewState = this.viewStatesByFile.get(file) ?? new AppsGroupsViewState();
    this.viewStatesByFile.set(file, this.viewState);
    this.clearFlowsFilter();

    this.isLoading = true;
    let selectedName = this.selectedGroup?.name;
    let groups = await this.fetchGroups(file);
    await Promise.all([this.inventoryLoaded, this.loadAllFiles()]);

    // Another file was picked while this one was loading
    if (file != this.selectedFile) {
      return false;
    }

    this.failureMode = groups == undefined;
    this.savedGroupsByFile.set(file, groups ?? []);
    let otherFiles = new Map(Array.from(this.savedGroupsByFile.entries()).filter(([other]) => other != file));
    this.editing = new AppsGroupsEditing(file, this.selectedFileIsGenerated, groups ?? [], otherFiles,
                                         this.inventory, this.netscopeService, () => this.ref.detectChanges());
    this.selectedGroup = this.editing.groups.find((group) => group.name == selectedName);
    this.focusSection = undefined;
    this.isLoading = false;

    await this.refreshMapGroups();
    return file == this.selectedFile;
  }

  // ---------------------------------------------------------------- Map

  /**
   * Gives the map the groups as they are edited, their inclusions resolved. It is only done when they changed
   * since the map last drew them, as the map lays everything out again.
   */
  refreshMapGroups = async () => {
    let content = JSON.stringify(this.editing.groups);
    if (content == this.mapContent && this.appsGroups != undefined) {
      return;
    }
    this.mapContent = content;

    let file = this.selectedFile;
    let groups = this.editing.groups.map((group) => AppsGroupsEditing.cleanCopy(group));
    setUuidForAppsGroups(groups);
    await resolveIncludedAppsGroups(this.netscopeService, file, groups);
    // Another file was opened meanwhile: its own groups are on their way to the map
    if (file != this.selectedFile || content != this.mapContent) {
      return;
    }
    this.appsGroups = groups;
  }

  selectTab = (tab: AppsGroupsViewerTab) => {
    let wasVisualisation = this.selectedTab == "visualisation";
    this.selectedTab = tab;

    if (tab == "visualisation" && !wasVisualisation) {
      let previousGroups = this.appsGroups;
      this.refreshMapGroups().then(() => {
        // New groups make the map draw itself again. Otherwise it is drawn again here: it cannot lay itself
        // out while it is hidden.
        if (this.appsGroups === previousGroups) {
          this.ref.detectChanges();
          this.map?.reloadUi();
        }
      });
    }
  }

  // Called from the cog of an apps group on the map
  onEditAppsGroup = (name: string) => {
    this.selectGroup(this.editing.groups.find((group) => group.name == name));
    this.selectTab("groups");
  }

  showOnMap = (group: AppsGroups) => {
    this.viewState.hiddenAppsGroups.delete(group.name);
    this.selectTab("visualisation");
  }

  // ---------------------------------------------------------------- Flows table

  // Flows of the flows table: the ones the map draws, which its renderer sets as it draws them
  get flowsRepository(): FlowsRepository {
    return this.flowsFilterGroup != undefined ? this.filteredFlowsRepository : this.map?.datatableTabFlowsRepository;
  }

  // Shows the flows of a group in the flows table: the ones one of its members took part in
  seeFlows = (group: AppsGroups) => {
    let source = this.map?.flowsRepository;
    let uuids = this.editing.members(group).map((member) => `'${member.uuid.replace(/'/g, "''")}'`);
    if (source == undefined || uuids.length == 0) {
      return;
    }

    let list = uuids.join(", ");
    this.filteredFlowsRepository = source.clone();
    this.filteredFlowsRepository.setWhereClause(`where (src_resource_uuid in (${list}) or dst_resource_uuid in (${list}))`);
    this.filteredFlowsRepository.showFirstPage();
    this.flowsFilterGroup = group.name;
    this.selectTab("flows");
  }

  clearFlowsFilter = () => {
    this.flowsFilterGroup = undefined;
    this.filteredFlowsRepository = undefined;
  }

  switchFocus = (vm, mode = "focus") => {
    this.netscopeUiService.switchFocus(vm, mode);
  }

  // ---------------------------------------------------------------- Groups

  get groupItems() {
    return this.editing.nestedGroups().map(({group, depth}) => {
      let members = this.editing.members(group);
      let count = (source: MemberSource) => members.filter((member) => member.source == source).length;
      let percent = (source: MemberSource) => members.length == 0 ? "0%" : `${(100 * count(source) / members.length).toFixed(2)}%`;

      let recipe = [];
      if ((group.vms ?? []).length > 0) {
        recipe.push(`${group.vms.length} picked`);
      }
      if ((group.members_rules?.include ?? []).length > 0) {
        recipe.push(group.members_rules.include.join(", "));
      }
      let includedCount = (group.included_apps_groups ?? []).length;
      if (includedCount > 0) {
        recipe.push(includedCount == 1 ? "1 group" : `${includedCount} groups`);
      }

      return {
        group: group,
        depth: depth,
        countLabel: members.length == 1 ? "1 member" : `${members.length} members`,
        recipe: recipe.length > 0 ? recipe.join(" · ") : "Empty",
        pickedPercent: percent("picked"),
        rulePercent: percent("rule"),
        includedPercent: percent("included"),
      };
    });
  }

  selectGroup = (group: AppsGroups, section: EditorSection = undefined) => {
    this.selectedGroup = group;
    this.focusSection = section;
  }

  // Opens a group the editor points to, in its own file
  openGroup = (ref: AppsGroupRef) => {
    this.openFile(ref.file, () => {
      this.selectGroup(this.editing.groups.find((group) => group.name == ref.name));
      this.selectedTab = "groups";
    });
  }

  descriptionsCountOf = (group: AppsGroups): number => {
    return this.descriptionsUsage.get(`${this.selectedFile}/${group.name}`) ?? 0;
  }

  onGroupDeleted = (group: AppsGroups) => {
    this.selectedGroup = undefined;
  }

  // Why a new group cannot be named this way in a file
  newGroupNameError = (name: string, file: string): string => {
    if (file == this.selectedFile) {
      return this.editing.nameError(undefined, name);
    }
    let error = this.editing.nameError(undefined, name);
    if (error != undefined && error.startsWith("Only")) {
      return error;
    }
    let taken = (this.savedGroupsByFile.get(file) ?? []).some((group) => group.name == name);
    return taken ? "Another group of this file has this name." : undefined;
  }

  createGroup = (request: NewAppsGroupRequest) => {
    this.openFile(request.file, () => {
      let group = this.editing.createGroup(request.name);
      this.selectGroup(group, request.way);
      this.selectedTab = "groups";
    });
  }

  /**
   * Copies a group of a generated file into a file of the user, where it can be changed. The groups it
   * includes stay the ones of the generated file.
   */
  duplicateGroup = (request: { group: AppsGroups, file: string }) => {
    let sourceFile = this.selectedFile;
    let copy = AppsGroupsEditing.cleanCopy(request.group);
    copy.included_apps_groups = (copy.included_apps_groups ?? [])
      .map((included) => ({name: included.name, file: included.file ?? sourceFile}));
    if (copy.included_apps_groups.length == 0) {
      delete copy.included_apps_groups;
    }
    copy.metadata = {};

    this.openFile(request.file, () => {
      copy.name = this.editing.freeName(copy.name);
      // Inclusions pointing back into the file the copy now belongs to name no file
      for (let included of copy.included_apps_groups ?? []) {
        if (included.file == request.file) {
          delete included.file;
        }
      }
      this.editing.groups.push(copy);
      this.editing.changed();
      this.selectGroup(copy);
      this.selectedTab = "groups";
    });
  }

  // ---------------------------------------------------------------- Saving

  get canSave(): boolean {
    return this.editing != undefined && this.editing.dirty && !this.editing.readOnly && !this.isSaving
      && this.editing.groups.every((group) => this.editing.nameError(group) == undefined
        && this.editing.patternsError(group) == undefined);
  }

  /**
   * Writes the displayed file back, then reads it again: the appliance computed the members matched by the
   * rules as it saved. True once saved.
   */
  save = async (): Promise<boolean> => {
    if (!this.canSave) {
      return false;
    }

    this.isSaving = true;
    this.saveError = undefined;
    let file = this.selectedFile;
    let response = await this.netscopeService.pushAppsGroups(file, this.editing.payload()).pipe(take(1)).toPromise();
    this.isSaving = false;

    if (!(response instanceof Success)) {
      this.saveError = "The file could not be saved.";
      return false;
    }

    this.filesNav?.setGroupsCount(file, this.editing.groups.length);
    this.savedGroupsByFile.delete(file);
    this.mapContent = undefined;
    if (!this.pendingFile) {
      await this.loadFile(file);
    } else {
      this.editing.dirty = false;
    }
    return true;
  }

  discard = () => {
    this.editing.discard();
    let selectedName = this.selectedGroup?.name;
    this.selectedGroup = this.editing.groups.find((group) => group.name == selectedName);
    this.saveError = undefined;
    if (this.selectedTab == "visualisation") {
      this.refreshMapGroups();
    }
  }

  // ---------------------------------------------------------------- Deleting the file

  // Groups of the other files including a group of the displayed one: they would point to missing groups
  get externalInclusionsCount(): number {
    let count = 0;
    this.savedGroupsByFile.forEach((groups, file) => {
      if (file != this.selectedFile) {
        count += groups.filter((group) => (group.included_apps_groups ?? [])
          .some((included) => included.file == this.selectedFile)).length;
      }
    });
    return count;
  }

  /**
   * Deletes the displayed file, its unsaved changes with it, then opens the file the page opens on, or the first
   * one left.
   */
  deleteFile = async () => {
    let file = this.selectedFile;
    this.isDeletingFile = true;
    this.saveError = undefined;
    let response = await this.netscopeService.deleteAppsGroupsFile(file).pipe(take(1)).toPromise();
    this.isDeletingFile = false;
    this.confirmFileDeletion = false;

    if (!(response instanceof Success)) {
      this.saveError = `The file ${file} could not be deleted.`;
      return;
    }

    this.editing.discard();
    this.savedGroupsByFile.delete(file);
    this.viewStatesByFile.delete(file);
    this.mapContent = undefined;
    this.selectedGroup = undefined;

    let remaining = this.files.map((item) => item.name).filter((name) => name != file);
    let next = remaining.includes("networks") ? "networks" : remaining[0];
    this.filesNav?.reloadFiles();
    if (next != undefined) {
      await this.loadFile(next);
    }
  }

  // ---------------------------------------------------------------- Flows descriptions

  private loadDescriptionsUsage = () => {
    this.netscopeService.getFlowsDescriptions().pipe(take(1)).subscribe((response) => {
      if (!(response instanceof Success)) {
        return;
      }

      let usage: Map<string, number> = new Map();
      for (let description of response.element?.flowsDescriptions ?? []) {
        let keys = new Set<string>();
        for (let rule of (description as any).networkRules ?? []) {
          for (let endpoint of (rule.sources ?? []).concat(rule.destinations ?? [])) {
            if (endpoint?.kind == "apps_group") {
              keys.add(`${endpoint.file}/${endpoint.name}`);
            }
          }
        }
        keys.forEach((key) => usage.set(key, (usage.get(key) ?? 0) + 1));
      }
      this.descriptionsUsage = usage;
    });
  }
}
